import { useState, useCallback, useMemo } from 'react';
import { PRODUCTS } from '../data/products';

const STORAGE_KEY = 'jg-cart';

const readCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeCart = (items) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
};

const useCart = () => {
  const [cartItems, setCartItems] = useState(readCart);

  const persist = useCallback((next) => {
    setCartItems(next);
    writeCart(next);
  }, []);

  const addToCart = useCallback((productId) => {
    const next = [...readCart()];
    const idx = next.findIndex((i) => i.productId === productId);
    if (idx >= 0) {
      next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
    } else {
      next.push({ productId, quantity: 1 });
    }
    persist(next);
  }, [persist]);

  const removeFromCart = useCallback((productId) => {
    const next = readCart().filter((i) => i.productId !== productId);
    persist(next);
  }, [persist]);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    const next = readCart().map((i) =>
      i.productId === productId ? { ...i, quantity } : i,
    );
    persist(next);
  }, [persist, removeFromCart]);

  const clearCart = useCallback(() => {
    persist([]);
  }, [persist]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, i) => sum + i.quantity, 0),
    [cartItems],
  );

  const cartTotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const product = PRODUCTS.find((p) => p.id === item.productId);
      return sum + (product ? product.price * item.quantity : 0);
    }, 0);
  }, [cartItems]);

  return { cartItems, addToCart, removeFromCart, updateQuantity, clearCart, cartCount, cartTotal };
};

export default useCart;
